import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { API, useAuth } from '@/App';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';
import { SEOHead, organizationSchema } from '@/components/SEOHead';
import { AdCarousel, SponsoredCard, FeaturedBanner, RecommendedStrip } from '@/components/FeedAds';
import { useGuestGate } from '@/hooks/useGuestGate';
import GuestGateModal from '@/components/GuestGateModal';
import {
  Search, X, GraduationCap, BookOpen, Plane, Heart, Home, Users, Target, Award,
  MapPin, Loader2, Filter, Zap, ArrowRight, Briefcase, Bookmark,
  Calendar, Gift, FileText, Sparkles, Shield, TrendingUp, Star, Newspaper
} from 'lucide-react';
import { getFieldColor, LEVEL_PALETTE } from '@/components/UniProfileShared';
import { roleFromToken, isPillAllowed, isAiChatAllowed } from '@/lib/roleGate';
import FeeDisplay from '@/components/FeeDisplay';

// Visual tokens — kept aligned with UniProfileShared so course cards in
// Explore look identical to the institution course wall.
const AMBER = '#F5A623';

const SERVICE_COLORS = {
  courses: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: BookOpen },
  colleges: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: GraduationCap },
  visa: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: Plane },
  jobs: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Briefcase },
  rpl: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Award },
  skills: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', icon: Target },
  insurance: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', icon: Heart },
  rentals: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Home },
  agents: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', icon: Users },
};

const DEFAULT_COVERS = {};

const GRADIENT_COVERS = {
  courses: 'from-blue-400 via-blue-500 to-indigo-600',
  colleges: 'from-violet-400 via-purple-500 to-fuchsia-600',
  visa: 'from-emerald-400 via-green-500 to-teal-600',
  jobs: 'from-orange-400 via-amber-500 to-yellow-500',
  rpl: 'from-rose-400 via-pink-500 to-fuchsia-500',
  skills: 'from-cyan-400 via-sky-500 to-blue-600',
  insurance: 'from-pink-400 via-rose-500 to-red-500',
  rentals: 'from-amber-400 via-orange-500 to-red-500',
  agents: 'from-teal-400 via-emerald-500 to-green-600',
};

const SERVICE_LABELS = {
  courses: 'Courses',
  agents: 'Agents',
  colleges: 'Colleges',
  visa: 'Visa',
  jobs: 'Jobs',
  rpl: 'RPL',
  skills: 'Skills',
  insurance: 'Insurance',
  rentals: 'Rentals',
};

function ServiceBadge({ type }) {
  const config = SERVICE_COLORS[type] || SERVICE_COLORS.courses;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${config.bg} ${config.text} ${config.border} border`}>
      <Icon className="h-2.5 w-2.5" />
      {SERVICE_LABELS[type] || type}
    </span>
  );
}

function FeedCard({ item, navigate }) {
  const { gate, sheetOpen, sheetTitle, setSheetOpen, returnUrl } = useGuestGate();
  const config = SERVICE_COLORS[item.service_type] || SERVICE_COLORS.courses;
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = item.image && item.image.length > 0 && !imgFailed;
  const gradient = GRADIENT_COVERS[item.service_type] || 'from-slate-400 to-slate-600';

  // Course-specific styling — mirror the institution course wall:
  //   • Solid colour header per subject (from getFieldColor)
  //   • Level pill (from LEVEL_PALETTE) using degree_type
  //   • 3px left border matching the level palette
  //   • Amber price
  //   • Bookmark icon + Apply Now → CTA
  const isCourse = item.service_type === 'courses';
  const lvl = isCourse ? LEVEL_PALETTE(item.badge) : null;
  const fieldColor = isCourse ? getFieldColor(item.extra) : null;

  return (
    <div
      onClick={() => navigate(item.link)}
      className={`bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg hover:shadow-slate-100/80 hover:-translate-y-0.5 transition-all duration-200 group relative ${
        item.is_sponsored
          ? 'border-2 border-amber-300 ring-2 ring-amber-100/40 shadow-md shadow-amber-100/40'
          : item.is_boosted
          ? 'border border-blue-200 ring-1 ring-blue-100/40'
          : 'border border-slate-100'
      }`}
      style={isCourse ? { borderLeft: `3px solid ${lvl.border}` } : undefined}
      data-testid={`feed-card-${item.service_type}-${item.id}`}
    >
      {/* Sponsored / Boosted ribbon */}
      {(item.is_sponsored || item.is_boosted) && (
        <div className={`absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider shadow-sm ${
          item.is_sponsored
            ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
            : 'bg-blue-500 text-white'
        }`} data-testid={item.is_sponsored ? 'sponsored-badge' : 'boosted-badge'}>
          {item.is_sponsored ? '★ Sponsored' : '↑ Boosted'}
        </div>
      )}
      {/* Image or solid/colour-gradient header (solid per subject for courses) */}
      {hasImage ? (
        <div className="h-32 overflow-hidden bg-slate-100">
          <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" onError={() => setImgFailed(true)} />
        </div>
      ) : isCourse ? (
        <div className="h-28 flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: fieldColor }}>
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          <BookOpen className="h-10 w-10 text-white drop-shadow-md" />
        </div>
      ) : (
        <div className={`h-28 bg-gradient-to-br ${gradient} flex items-center justify-center relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          <config.icon className="h-10 w-10 text-white drop-shadow-md" />
        </div>
      )}

      <div className="p-3 space-y-1.5 min-w-0">
        <div className="flex items-center justify-between gap-1.5 min-w-0">
          {isCourse ? (
            item.badge ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                style={{ backgroundColor: lvl.bg, border: `1px solid ${lvl.border}`, color: lvl.text }}>
                {item.badge}
              </span>
            ) : (
              <ServiceBadge type={item.service_type} />
            )
          ) : item.service_type === 'agents' ? (
            // Agents get a single, untruncated "✅ MARA Verified" pill on top.
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-bold whitespace-nowrap">
              <span aria-hidden>✅</span> {item.is_mara_verified ? 'MARA Verified' : (item.badge || 'Verified Agent')}
            </span>
          ) : item.service_type === 'colleges' && item.top50 ? (
            // Top-50 universities get an amber prestige pill that overrides
            // the generic service badge.
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-extrabold">
              <span aria-hidden>★</span> {item.badge}
            </span>
          ) : (
            <>
              <ServiceBadge type={item.service_type} />
              {item.badge && (
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider shrink-0 truncate max-w-[50%]">{item.badge}</span>
              )}
            </>
          )}
        </div>

        <h3 className="text-sm font-bold text-slate-800 line-clamp-2 leading-tight group-hover:text-[#2F80ED] transition-colors">
          {item.title}
        </h3>

        {item.subtitle && (
          <p className="text-xs text-slate-500 line-clamp-1">{item.subtitle}</p>
        )}

        {/* Audience pill — courses only, when restricted to a single audience.
            Default 'both' renders nothing. */}
        {isCourse && item.audience && item.audience !== 'both' && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider w-fit ${
              item.audience === 'domestic'
                ? 'bg-violet-50 text-violet-700 border border-violet-100'
                : 'bg-orange-50 text-orange-700 border border-orange-100'
            }`}
            data-testid={`feed-audience-${item.id}`}
          >
            {item.audience === 'domestic' ? 'Domestic only' : 'International only'}
          </span>
        )}

        {/* Agent rating row — shown only when rating data exists. */}
        {item.service_type === 'agents' && item.rating && (
          <div className="flex items-center gap-1 text-[11px] font-bold text-amber-700" data-testid={`agent-rating-${item.id}`}>
            <span aria-hidden>⭐</span>
            <span>{item.rating}</span>
            {item.review_count && <span className="text-slate-400 font-normal">· {item.review_count} review{item.review_count !== 1 ? 's' : ''}</span>}
          </div>
        )}

        {/* Agent specializations — max 2 separated by · */}
        {item.service_type === 'agents' && item.extra && (
          <p className="text-[11px] text-slate-500 line-clamp-1" data-testid={`agent-specs-${item.id}`}>{item.extra}</p>
        )}

        {/* College extra (courses + CRICOS) */}
        {item.service_type === 'colleges' && item.extra && (
          <p className="text-[11px] text-slate-500 line-clamp-1 font-medium" data-testid={`college-extra-${item.id}`}>{item.extra}</p>
        )}

        <div className="flex items-center justify-between pt-1">
          {isCourse ? (
            <FeeDisplay
              course={{
                course_id: item.id,
                name: item.title,
                university_id: item.university_id,
                university_name: item.subtitle,
                fee_display: item.fee_display,
                display_fee_aud: item.display_fee_aud,
              }}
              amountSize="text-sm"
            />
          ) : item.price ? (
            <span className="text-xs font-bold text-[#2F80ED]">{item.price}</span>
          ) : null}
          {item.location && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
              <MapPin className="h-2.5 w-2.5" />
              {item.location}
            </span>
          )}
        </div>

        {/* Course-only CTA row — Apply Now + Save (bookmark) — mirrors the institution wall */}
        {isCourse && (
          <div className="flex gap-1.5 pt-1">
            <button
              onClick={(e) => { e.stopPropagation(); gate('Sign up to apply', () => navigate(item.link)); }}
              className="flex-1 py-1.5 rounded-lg bg-[#2F80ED] text-white text-[11px] font-bold hover:bg-[#1A6BD4] transition-colors"
              data-testid={`explore-apply-${item.id}`}
            >
              Apply Now →
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); gate('Sign up to save this', () => navigate(item.link)); }}
              className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              data-testid={`explore-save-${item.id}`}
              title="Save this course"
            >
              <Bookmark className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {sheetOpen && (
        <GuestGateModal
          title={sheetTitle}
          returnUrl={returnUrl}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ACTION HUB — shown only on Explore > All when search is empty.
   Reuses FeaturedBanner + FeedCard + AdCarousel + AI nudge style.
   ───────────────────────────────────────────────────────────── */

const QUICK_FILTERS = [
  { label: 'Nursing in Australia', q: 'nursing', service: 'courses' },
  { label: 'Courses under $20k', q: 'under 20000', service: 'courses' },
  { label: 'PR Pathway Courses', q: 'pr pathway', service: 'courses' },
  { label: 'Feb 2026 Intake', q: 'feb 2026 intake', service: 'courses' },
];

function ActionCardsGrid({ onPickService }) {
  // 4 big tiles — primary entry surface for the All tab. Tile labels and
  // gradients reuse the same SERVICE_COLORS palette already used by
  // service-pill chips below, so visual hierarchy stays consistent.
  // We call onPickService (which delegates to handleServiceFilter) instead of
  // navigate() — the latter just updates URL params without triggering
  // activeService state change because useSearchParams is only read once.

  // Jun 28, 2026 — for agent / institution / recruiter logged-in users we
  // HIDE THE "Browse" BUTTON on the Talk-to-Experts tile (the tile itself
  // stays visible but is non-clickable). This blocks the last surface
  // where agents could still discover other agents.
  const role = roleFromToken();
  const hideAgentCTA = role === 'agent' || role === 'institution' || role === 'recruiter';

  const cards = [
    { id: 'find-course', title: 'Find Best Course', sub: 'Discover courses that fit you', cta: 'Start', icon: GraduationCap, grad: 'from-violet-50 to-violet-100', text: 'text-violet-700', cta_bg: 'bg-violet-600 hover:bg-violet-700', svc: 'courses' },
    { id: 'explore-colleges', title: 'Explore Colleges', sub: 'Compare universities & find the best', cta: 'Explore', icon: BookOpen, grad: 'from-blue-50 to-blue-100', text: 'text-blue-700', cta_bg: 'bg-blue-600 hover:bg-blue-700', svc: 'colleges' },
    { id: 'talk-experts', title: 'Talk to Experts (Browse Agents)', sub: 'Connect with Unexa verified Agents', cta: 'Browse', icon: Users, grad: 'from-emerald-50 to-emerald-100', text: 'text-emerald-700', cta_bg: 'bg-emerald-600 hover:bg-emerald-700', svc: 'agents', hideCTAForRoles: hideAgentCTA },
    { id: 'visa-pathway', title: 'PR / Visa Pathway', sub: 'Check eligibility & plan your journey', cta: 'Check Now', icon: Plane, grad: 'from-orange-50 to-orange-100', text: 'text-orange-700', cta_bg: 'bg-orange-600 hover:bg-orange-700', svc: 'visa' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 mb-4" data-testid="action-cards-grid">
      {cards.map(c => {
        const Icon = c.icon;
        const ctaHidden = !!c.hideCTAForRoles;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => { if (!ctaHidden) onPickService(c.svc); }}
            className={`relative text-left rounded-2xl p-4 bg-gradient-to-br ${c.grad} transition-all min-h-[180px] flex flex-col justify-between ${ctaHidden ? 'cursor-default' : 'hover:shadow-lg hover:-translate-y-0.5 active:scale-95'}`}
            data-testid={`action-card-${c.id}`}
          >
            <div>
              <Icon className={`h-7 w-7 ${c.text} mb-2`} strokeWidth={2.5} />
              <h3 className={`text-sm font-black ${c.text} leading-tight mb-1`}>{c.title}</h3>
              <p className="text-[11px] text-slate-600 leading-snug">{c.sub}</p>
            </div>
            {!ctaHidden && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onPickService(c.svc); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPickService(c.svc); } }}
                className={`mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold text-white ${c.cta_bg} w-fit shadow-sm cursor-pointer`}
                data-testid={`action-card-cta-${c.id}`}
              >
                {c.cta} <ArrowRight className="h-3 w-3" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function DeadlinesStrip() {
  // Reuses existing news endpoint — filter urgency=high. Backend now caches
  // this exact query for 30min (Mar 1, 2026) so first paint is fast.
  // 5-second timeout: if the request hangs (network blip), silently hide
  // the strip instead of blocking the page.
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      if (alive && items === null) setFailed(true);
    }, 5000);
    axios.get(`${API}/news/articles`, { params: { urgency: 'high' } })
      .then(r => {
        if (!alive) return;
        const arr = (r.data?.articles || r.data || []).slice(0, 3);
        setItems(arr);
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) clearTimeout(timer); });
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  // Silent hide on timeout — never show a full-page error.
  if (failed && items === null) return null;

  // Skeleton while loading — 3 placeholder cards matching final layout
  // exactly, prevents layout shift when real data arrives.
  if (items === null) {
    return (
      <div className="bg-amber-50/40 border border-amber-100 rounded-2xl p-3 mb-3" data-testid="deadlines-strip-skeleton">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-black text-amber-900 flex items-center gap-1.5">
            <span className="text-base">⏰</span> Deadlines are coming up!
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-2.5 border border-amber-100/60">
              <div className="w-7 h-7 rounded-lg bg-slate-100 animate-pulse mb-1.5" />
              <div className="h-2.5 w-3/4 rounded bg-slate-100 animate-pulse" />
              <div className="h-2 w-full rounded bg-slate-100 animate-pulse mt-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cards = (items && items.length > 0) ? [
    { icon: Calendar, label: items[0]?.category || 'July 2026 Intake', desc: items[0]?.title?.slice(0, 40) || 'Applications closing soon', color: 'text-amber-700 bg-amber-50' },
    { icon: Gift, label: items[1]?.category || 'Scholarships', desc: items[1]?.title?.slice(0, 40) || 'Up to $5,000 ending soon', color: 'text-emerald-700 bg-emerald-50' },
    { icon: FileText, label: items[2]?.category || 'Visa Updates', desc: items[2]?.title?.slice(0, 40) || 'Australia rules changed', color: 'text-rose-700 bg-rose-50' },
  ] : [
    { icon: Calendar, label: 'July 2026 Intake', desc: 'Applications closing in 18 days', color: 'text-amber-700 bg-amber-50' },
    { icon: Gift, label: 'Scholarships', desc: 'Up to $5,000 ending soon', color: 'text-emerald-700 bg-emerald-50' },
    { icon: FileText, label: 'Visa Updates', desc: 'Australia rules changed', color: 'text-rose-700 bg-rose-50' },
  ];
  return (
    <div className="bg-amber-50/40 border border-amber-100 rounded-2xl p-3 mb-3" data-testid="deadlines-strip">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-black text-amber-900 flex items-center gap-1.5">
          <span className="text-base">⏰</span> Deadlines are coming up!
        </p>
        <Link to="/news" className="text-[11px] font-bold text-amber-700 hover:underline">View all</Link>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="bg-white rounded-xl p-2.5 border border-amber-100/60" data-testid={`deadline-card-${i}`}>
              <div className={`w-7 h-7 rounded-lg ${c.color} flex items-center justify-center mb-1.5`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <p className="text-[10px] font-black text-slate-900 leading-tight">{c.label}</p>
              <p className="text-[9px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{c.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HubAgentMini({ agent }) {
  const initials = (agent.name || 'M').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <Link
      to={`/agents/${agent.user_id}`}
      className="shrink-0 w-[150px] flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white border border-slate-100 hover:border-emerald-300 hover:shadow-md transition-all"
      data-testid={`hub-agent-${agent.user_id}`}
    >
      {agent.profile_picture ? (
        <img src={agent.profile_picture} alt={agent.name} className="w-14 h-14 rounded-full object-cover border-2 border-emerald-100" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-sm font-bold flex items-center justify-center border-2 border-emerald-100">{initials}</div>
      )}
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-black bg-emerald-50 text-emerald-700">
        <Shield className="h-2 w-2" /> VERIFIED
      </span>
      <p className="text-[11px] font-black text-slate-900 text-center truncate w-full">{agent.name}</p>
      <p className="text-[9px] text-slate-400 truncate w-full text-center">★ {(agent.avg_rating || agent.rating || 4.7).toFixed(1)} · {agent.years_experience || '5+'} yrs</p>
      <span className="mt-1 px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold">Chat Now</span>
    </Link>
  );
}

function ActionHub({ navigate, items, setSearch, setActiveService, sortBy, fetchFeed }) {
  // Role gate — sync from JWT (Feb 2026). Hides the AI CTA card and the
  // "Talk to Experts" agent carousel for agent/institution users.
  const role = roleFromToken();
  const aiAllowed = isAiChatAllowed(role);
  const agentsPillAllowed = isPillAllowed(role, 'agents');
  // items = the first page of /explore/feed (already loaded for the grid
  // below). We slice it to power Top Matches + Recommended without an
  // extra API call — true "reuse data we already have".
  const { user } = useAuth();
  const isGuest = !user;
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/agents/directory`, { params: { verified: true, limit: 5 } })
      .then(r => setAgents((r.data?.agents || r.data || []).slice(0, 5)))
      .catch(() => setAgents([]));
  }, []);

  const topMatches = items.slice(0, 3);
  const recommended = items.slice(3, 6);

  const applyQuickFilter = (qf) => {
    setActiveService(qf.service || 'all');
    setSearch(qf.q || '');
  };

  return (
    <div data-testid="action-hub">
      {/* 1. Action Cards */}
      <div className="mb-1">
        {/* Guests see the conversion-optimized heading + stats proof line;
            logged-in users keep the action-oriented heading. Set on Mar 1, 2026
            when ExplorePage was promoted to the new homepage. */}
        <h2 className="text-lg font-black text-slate-900 mb-1" data-testid="explore-heading">
          {isGuest ? 'Where do you want to study?' : 'What would you like to do today?'}
        </h2>
        {isGuest ? (
          <p className="text-xs text-slate-500 mb-3 flex items-center gap-1.5" data-testid="guest-stats-subtitle">
            <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />
            26,000+ courses across 1,558 colleges · 4,000+ MARA verified agents
          </p>
        ) : (
          <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
            Take the next step towards your future <Sparkles className="h-3 w-3 text-amber-500" />
          </p>
        )}
      </div>
      <ActionCardsGrid onPickService={setActiveService} />

      {/* Content Hub pills — Blog (left) + Newsboard (right). Sits in the
          gap between action cards and the Deadlines strip. */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="content-hub-pills">
        <button
          type="button"
          onClick={() => navigate('/blog')}
          className="flex items-center justify-between px-4 py-2.5 rounded-full bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all active:scale-95 group"
          data-testid="content-pill-blog"
        >
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-violet-600" strokeWidth={2.5} />
            <span className="text-xs font-bold text-slate-800">Blog</span>
            <span className="text-[10px] text-slate-400 hidden sm:inline">Guides & insights</span>
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition-all" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/blog?tab=news')}
          className="flex items-center justify-between px-4 py-2.5 rounded-full bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all active:scale-95 group"
          data-testid="content-pill-newsboard"
        >
          <span className="flex items-center gap-2">
            <Newspaper className="h-4 w-4 text-orange-600" strokeWidth={2.5} />
            <span className="text-xs font-bold text-slate-800">Newsboard</span>
            <span className="text-[10px] text-slate-400 hidden sm:inline">Latest visa & immigration</span>
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-orange-600 group-hover:translate-x-0.5 transition-all" />
        </button>
      </div>

      {/* 2. Deadlines Strip */}
      <DeadlinesStrip />

      {/* 3. Quick Search Chips */}
      <div className="mb-3">
        <p className="text-xs font-bold text-slate-700 mb-2">Quick Start <span className="text-slate-400 font-medium">(Popular Searches)</span></p>
        <div className="flex flex-wrap gap-2">
          {QUICK_FILTERS.map(qf => (
            <button
              key={qf.label}
              onClick={() => applyQuickFilter(qf)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
              data-testid={`quick-filter-${qf.q.replace(/\s+/g,'-')}`}
            >
              {qf.label} <Search className="h-3 w-3 text-slate-400" />
            </button>
          ))}
        </div>
      </div>

      {/* 4. Featured Opportunity — first monetisation slot */}
      <div className="mb-4" data-testid="hub-featured">
        <FeaturedBanner section="colleges" index={0} />
      </div>

      {/* 5. Top Matches */}
      {topMatches.length > 0 && (
        <div className="mb-4" data-testid="hub-top-matches">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-black text-slate-900">Top Matches for You</h3>
            <button onClick={() => setActiveService('courses')} className="text-[11px] font-bold text-[#2F80ED] hover:underline">View all</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {topMatches.map(item => (
              <div key={`tm-${item.service_type}-${item.id}`} className="relative">
                <span className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[8px] font-black uppercase tracking-wide">
                  <Star className="h-2 w-2" /> Top Match
                </span>
                <FeedCard item={item} navigate={navigate} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Recommended for You — second monetisation slot embedded */}
      {recommended.length > 0 && (
        <div className="mb-4" data-testid="hub-recommended">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-black text-slate-900">Recommended for You</h3>
            <button onClick={() => setActiveService('courses')} className="text-[11px] font-bold text-[#2F80ED] hover:underline">View all</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {recommended.map((item, i) => (
              <React.Fragment key={`rec-${item.service_type}-${item.id}`}>
                {i === 1 && <SponsoredCard section="colleges" index={0} />}
                <div className="relative">
                  <span className={`absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-white text-[8px] font-black uppercase tracking-wide ${i === 0 ? 'bg-violet-500' : 'bg-orange-500'}`}>
                    {i === 0 ? <><TrendingUp className="h-2 w-2" /> Popular</> : <><Zap className="h-2 w-2" /> High Demand</>}
                  </span>
                  <FeedCard item={item} navigate={navigate} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* 7. Talk to Experts — agent carousel (hidden for agents) */}
      {agentsPillAllowed && agents.length > 0 && (
        <div className="mb-4" data-testid="hub-experts">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-black text-slate-900">Talk to Experts</h3>
              <p className="text-[11px] text-slate-500">Connect with verified education consultants who can guide you.</p>
            </div>
            <button onClick={() => setActiveService('agents')} className="text-[11px] font-bold text-emerald-600 hover:underline">View all</button>
          </div>
          <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
            {agents.map(a => <HubAgentMini key={a.user_id} agent={a} />)}
          </div>
        </div>
      )}

      {/* 8. AI CTA — single static card (hidden for agents/institutions) */}
      {aiAllowed && (
      <button
        onClick={() => navigate('/ai')}
        className="w-full text-left mb-4 rounded-2xl p-4 bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:shadow-xl active:scale-[0.98] transition-all"
        data-testid="hub-ai-cta"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-black text-white">Not sure what's best for you?</p>
            <p className="text-[11px] text-white/90 mt-0.5">Ask Unexa AI and get personalised recommendations in seconds.</p>
            <span className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-violet-700 text-[11px] font-black">
              Ask Unexa AI <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      </button>
      )}

      {/* 9. Important Updates */}
      <div className="mb-4" data-testid="hub-updates">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-black text-slate-900">Important Updates</h3>
          <Link to="/news" className="text-[11px] font-bold text-[#2F80ED] hover:underline">View all</Link>
        </div>
        <HubUpdatesList />
      </div>

      {/* 10. Why Unexa — static info blocks (lowest priority) */}
      <div className="mb-4" data-testid="hub-why-unexa">
        <h3 className="text-sm font-black text-slate-900 mb-2">Why students love Unexa</h3>
        <div className="space-y-2">
          {[
            { icon: Shield, color: 'text-violet-600 bg-violet-50', title: 'Compare & Shortlist', desc: 'Easily compare courses and colleges' },
            { icon: Users, color: 'text-emerald-600 bg-emerald-50', title: 'Expert Guidance', desc: 'Connect with verified education experts' },
            { icon: Star, color: 'text-orange-600 bg-orange-50', title: 'High Success Rate', desc: 'Students get placed in top universities' },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
                <div className={`w-10 h-10 rounded-xl ${b.color} flex items-center justify-center shrink-0`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-slate-900">{b.title}</p>
                  <p className="text-[11px] text-slate-500 leading-snug">{b.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* "More content below" divider — grid follows */}
      <p className="text-center text-[11px] text-slate-400 mb-3 mt-2">More content below ↓</p>
    </div>
  );
}

function HubUpdatesList() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/news/articles`)
      .then(r => setItems((r.data?.articles || r.data || []).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      {items.map(n => (
        <Link
          key={n.article_id || n.id || n.slug}
          to={n.slug ? `/blog/${n.slug}` : '/news'}
          className="flex items-start gap-3 bg-white rounded-xl p-3 border border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all"
          data-testid={`hub-update-${n.article_id || n.id || n.slug}`}
        >
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-slate-900 line-clamp-1">{n.seo_title || n.title}</p>
            <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5">{n.seo_description || n.meta_description || n.summary || ''}</p>
          </div>
          {n.published_at && (
            <p className="text-[9px] text-slate-300 shrink-0">{new Date(n.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
          )}
        </Link>
      ))}
    </div>
  );
}

export default function ExplorePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { isEnabled, loaded: flagsLoaded } = usePlatformFlags();

  // Role gate (Feb 2026) — synchronous from JWT so the wrong pill never
  // flashes on cold load:
  //   • agent       → hide "Agents" pill (they ARE agents)
  //   • institution → hide "Colleges" pill (they ARE colleges)
  //   • agent / institution → hide AI CTA card
  const role = user?.role || roleFromToken();
  const aiAllowed = isAiChatAllowed(role);

  // Note: Role-based "go to your dashboard" redirect lives in AuthPage.jsx
  // (post-login). Once logged in, role users (admin/agent/institution/
  // recruiter) are free to browse Explore via the bottom nav. We removed
  // the in-page bounce here on Mar 2, 2026 because it was stopping agents
  // and institutions from accessing /explore at all — every visit bounced
  // them back to their dashboard.

  const [items, setItems] = useState([]);
  usePrerenderReady(items.length > 0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState('featured');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [activeService, setActiveService] = useState(searchParams.get('service') || 'all');
  const [serviceCounts, setServiceCounts] = useState({});
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const sentinelRef = React.useRef(null);
  const fetchIdRef = React.useRef(0);
  const [nudge, setNudge] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // ─── Fast path: pill counts load INDEPENDENTLY of the heavy feed query.
  // Frontend hits the lightweight /explore/counts endpoint on mount so the
  // 4 pills (Explore / Courses / Agents / Colleges) render in ~50ms even
  // when the main feed pipeline takes 2-3s. Cached server-side 5 min +
  // Cloudflare edge cache. Added Jun 5, 2026.
  //
  // Auto-refresh every 60s (Feb 2026): keep counts current as new courses
  // / colleges / agents land in Atlas. Only polls when the tab is visible
  // so background tabs don't hammer Atlas — saves ~95% of API calls vs
  // naive polling. Pauses immediately on hide, resumes on show.
  //
  // Resilience (Feb 2026 hotfix): NEVER overwrite displayed counts with
  // a suspicious all-zero / mostly-zero response. Production saw "Flow
  // goes blank" because a transient Atlas blip caused /counts to return
  // {colleges:0, courses:0, ...} and the frontend faithfully rendered
  // it. New rule: only commit a counts payload if (a) it's the first
  // load (state is empty), OR (b) the new total is not dramatically
  // smaller than the last-known total. Otherwise we keep last-good and
  // try again next tick.
  useEffect(() => {
    const isSuspicious = (newCounts, currentCounts) => {
      if (!newCounts || typeof newCounts !== 'object') return true;
      const newTotal = Object.values(newCounts).reduce((a, b) => a + (b || 0), 0);
      const curTotal = Object.values(currentCounts || {}).reduce((a, b) => a + (b || 0), 0);
      // First load — accept whatever we got (even zeros).
      if (curTotal === 0) return false;
      // If new total is < 10% of current, the response is almost
      // certainly degraded (Atlas blip / partial failure). Drop it.
      return newTotal < curTotal * 0.1;
    };

    const loadCounts = () => axios.get(`${API}/explore/counts`, { timeout: 8000 })
      .then(r => {
        const incoming = r.data?.service_counts || {};
        setServiceCounts(prev => {
          if (isSuspicious(incoming, prev)) {
            // Don't blank the UI — keep last-known-good counts.
            // The next 60s tick will retry.
            return prev;
          }
          return incoming;
        });
        setCountsLoaded(true);
      })
      .catch(() => { /* keep last-good counts; transient failures shouldn't blank the UI */ });

    loadCounts();
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') loadCounts();
      }, 60_000);
    };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVis = () => {
      if (document.visibilityState === 'visible') { loadCounts(); start(); }
      else stop();
    };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // Client-side cache: { 'all::': {items, counts, total, hasMore, ts}, 'colleges::': ... }
  const cacheRef = React.useRef({});
  const CACHE_TTL = 60000; // 60 seconds

  const getCacheKey = (service, query) => `${service || 'all'}::${query || ''}`;

  // Shuffle seed — held in a ref so it's stable while the user paginates
  // (load-more). Rotates only when the user actively taps a different pill.
  // First-visit: no seed is sent — backend uses an hour-bucket fallback so
  // the response is shared across all guests in the hour, allowing the
  // edge/in-memory cache to deliver a 110ms hit instead of a 1.5s Mongo
  // round-trip. A seed is only generated AFTER the user explicitly rotates
  // (tap pill again) for personalised reshuffling.
  const shuffleSeedsRef = useRef({});
  const getShuffleSeed = (service) => shuffleSeedsRef.current[service] || null;
  const rotateShuffleSeed = (service) => {
    shuffleSeedsRef.current[service] = Math.floor(Math.random() * 2_000_000_000);
    // Also invalidate the page-1 cache for this service so the new shuffle
    // is honoured immediately (otherwise the user sees yesterday's order).
    Object.keys(cacheRef.current).forEach(k => {
      if (k.startsWith(`${service}::`) || k.startsWith(`all::`)) delete cacheRef.current[k];
    });
  };

  const fetchFeed = useCallback(async (query, service, pageNum = 1, append = false, sortKey = null) => {
    const fetchId = ++fetchIdRef.current;
    const seed = getShuffleSeed(service || 'all');
    const cacheKey = `${getCacheKey(service, query)}::${sortKey || 'featured'}::${seed || 'cold'}`;

    // Return cached results for page 1 (non-append) if fresh
    if (pageNum === 1 && !append && cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      if (Date.now() - cached.ts < CACHE_TTL) {
        setItems(cached.items);
        setServiceCounts(cached.counts);
        setCountsLoaded(true);
        setHasMore(cached.hasMore);
        setTotalCount(cached.total);
        setLoading(false);
        return;
      }
    }

    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { page: pageNum, limit: 30 };
      // Only attach a seed when one has been generated (i.e. user actively
      // rotated). Omitting it lets the backend cache the response.
      if (seed) params.seed = seed;
      if (query) params.q = query;
      if (service && service !== 'all') params.service = service;
      if (sortKey && sortKey !== 'featured') params.sort = sortKey;
      const res = await axios.get(`${API}/explore/feed`, { params });
      if (fetchIdRef.current !== fetchId) return;
      const newItems = res.data.items || [];
      if (append) {
        setItems(prev => [...prev, ...newItems]);
      } else {
        // Resilience (Feb 2026): on a page-1 reload, if the backend
        // returned an unexpectedly empty list AND we already have items
        // displayed AND no filter/search changed, treat it as a
        // degraded response (Atlas blip) and KEEP the old list. The
        // next fetch (filter tap, scroll, or 60s auto-refresh) will
        // restore. Initial load (items.length === 0) is always honored.
        if (newItems.length === 0 && items.length > 0 && !query && (res.data.total ?? 0) > 0) {
          // Degraded — keep last-good. Don't update items / counts.
        } else {
          setItems(newItems);
          // Cache page 1 results
          cacheRef.current[cacheKey] = {
            items: newItems,
            counts: res.data.service_counts || {},
            total: res.data.total || 0,
            hasMore: res.data.has_more ?? false,
            ts: Date.now(),
          };
        }
      }
      // Only commit counts when they look healthy — otherwise keep
      // whatever we had on screen.
      const incomingCounts = res.data.service_counts || {};
      setServiceCounts(prev => {
        const incTotal = Object.values(incomingCounts).reduce((a, b) => a + (b || 0), 0);
        const curTotal = Object.values(prev || {}).reduce((a, b) => a + (b || 0), 0);
        if (curTotal > 0 && incTotal < curTotal * 0.1) return prev;
        return incomingCounts;
      });
      setCountsLoaded(true);
      setHasMore(res.data.has_more ?? false);
      setTotalCount(res.data.total || 0);
    } catch {
      if (fetchIdRef.current !== fetchId) return;
      // Resilience (Feb 2026): on network error / 520, keep the existing
      // list on screen instead of blanking. Only blank on initial load.
      if (!append && items.length === 0) setItems([]);
    } finally {
      if (fetchIdRef.current === fetchId) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // Separate effects for search (debounced) vs pill switch (instant)
  const searchTimerRef = React.useRef(null);
  const prevSearchRef = React.useRef(search);
  const prevServiceRef = React.useRef(activeService);

  useEffect(() => {
    const searchChanged = search !== prevSearchRef.current;
    const serviceChanged = activeService !== prevServiceRef.current;
    prevSearchRef.current = search;
    prevServiceRef.current = activeService;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    const doFetch = () => {
      setPage(1);
      fetchFeed(search, activeService, 1, false, sortBy);
      if (search.trim()) {
        setNudgeDismissed(false);
        axios.get(`${API}/explore/ai-nudge`, { params: { q: search } })
          .then(res => setNudge(res.data?.nudge || null))
          .catch(() => setNudge(null));
      } else {
        setNudge(null);
      }
    };

    // Pill switch = instant, search typing = debounced
    if (serviceChanged && !searchChanged) {
      doFetch();
    } else if (searchChanged) {
      const delay = search ? 300 : 0;
      searchTimerRef.current = setTimeout(doFetch, delay);
    } else {
      doFetch();
    }
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search, activeService, sortBy, fetchFeed]);

  // Auto-switch active pill when it goes to zero during a search.
  // Picks the first non-zero pill (preferring a popular default order) so the
  // user always lands on a tab that has matching results.
  useEffect(() => {
    if (!countsLoaded) return;
    if (!search || !search.trim()) return;
    if (activeService === 'all') return;
    const currentCount = serviceCounts[activeService] || 0;
    if (currentCount > 0) return;
    const fallbackOrder = ['courses', 'colleges', 'agents', 'jobs', 'visa', 'rentals', 'insurance', 'rpl', 'skills'];
    const next = fallbackOrder.find(k => (serviceCounts[k] || 0) > 0);
    setActiveService(next || 'all');
  }, [serviceCounts, countsLoaded, search, activeService]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchFeed(search, activeService, nextPage, true, sortBy);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, search, activeService, sortBy, fetchFeed]);

  const handleClearSearch = () => {
    setSearch('');
  };

  const handleServiceFilter = (svc) => {
    // Fresh random seed on EVERY pill click — same pill or different
    // pill — so users get a truly new shuffle on each tap (Feb 2026
    // fix). Pagination within the same tap session reuses the seed
    // (rotateShuffleSeed only fires on the click itself, not on
    // load-more) so page 1→2→3 stays consistent.
    rotateShuffleSeed(svc);
    if (svc === activeService) {
      // Same pill — setActiveService alone wouldn't re-run the effect.
      fetchFeed(search, svc, 1, false, sortBy);
    } else {
      setActiveService(svc);
    }
  };

  const allTotal = Object.values(serviceCounts).reduce((a, b) => a + b, 0);

  // Map active filter to ad section name
  const adSection = activeService === 'all' ? 'colleges' : activeService;

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="explore-page">
      <SEOHead
        title="Unexa AI — Study in Australia | Courses, Visa, PR, Jobs, Housing & Community"
        description="Compare 26,000+ courses across 1,558 Australian colleges. Find MARA verified migration agents, visa pathways, scholarships, housing & jobs — all in one place on Unexa."
        path="/"
        structuredData={organizationSchema()}
      />

      <div className="px-4 pt-2 pb-20">
        {/* Search Bar */}
        <div className="mb-3">
          <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-slate-200 hover:border-slate-300 focus-within:border-[#2F80ED] focus-within:ring-2 focus-within:ring-blue-50 transition-all px-3 py-2.5">
            <Search className="h-4 w-4 text-slate-400 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search across all services..."
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400 text-slate-700"
              data-testid="explore-search-input"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="explore-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* AI FOMO Nudge Slide */}
        {nudge && !nudgeDismissed && (
          <div className="mb-3 animate-slideDown" data-testid="ai-nudge-slide">
            <div className="relative bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-2xl px-4 py-3 shadow-lg shadow-violet-200/40">
              <button
                onClick={() => setNudgeDismissed(true)}
                className="absolute top-2 right-2 text-white/50 hover:text-white transition-colors"
                data-testid="ai-nudge-dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-start gap-2.5 pr-4">
                <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Zap className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] leading-relaxed text-white/90 font-medium">{nudge.message}</p>
                  <button
                    onClick={() => navigate(nudge.cta_link)}
                    className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white text-violet-700 text-[11px] font-bold hover:bg-white/90 active:scale-95 transition-all"
                    data-testid="ai-nudge-cta"
                  >
                    {nudge.cta_text} <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Service Filter Pills — free-scroll on mobile WITH gentle auto-scroll
            that pauses on user interaction. Wraps normally on desktop (≥ lg).
            The "Explore (N)" reset pill (`all` prop) is hidden by request —
            the 'all' filter is still the default selection on page load; users
            switch back to it by clicking the currently active pill twice or
            via search reset. */}
        <AutoScrollPills>
          {/* Skeleton pills while counts are loading (countsLoaded=false).
              Prevents layout shift when real counts arrive — same width/
              height as real pills, pulse animation, no API of their own. */}
          {!countsLoaded && (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`pill-skeleton-${i}`}
                  className="shrink-0 px-4 py-2 rounded-full bg-slate-100 animate-pulse"
                  style={{ width: 90 + i * 12 }}
                  data-testid={`filter-pill-skeleton-${i}`}
                >
                  <span className="opacity-0">.</span>
                </div>
              ))}
            </>
          )}
          {/* Render service-specific pills only AFTER platform flags have
              loaded — prevents a flash where all pills appear and then some
              disappear when admin-disabled flags arrive. The "Explore" pill
              above is always visible so the bar isn't empty during load.

              Resilience (Feb 2026): also hide pills whose count is 0 when
              there's no active search and the pill isn't currently
              selected. This survives two failure modes:
                • Platform-flags endpoint returns 520 → isEnabled() falls
                  back to "enabled" → previously rendered "Jobs (0)" etc.
                • Service is genuinely empty → no reason to show a dead
                  pill that nobody can act on.
              The currently-active pill always stays visible so the
              selection state remains coherent. */}
          {flagsLoaded && countsLoaded && Object.entries(SERVICE_LABELS).map(([key, label]) => {
            if (!isEnabled(key)) return null;
            // Role gate — Feb 2026.
            if (!isPillAllowed(role, key)) return null;
            const count = serviceCounts[key] || 0;
            const hasSearch = !!(search && search.trim());
            // Hide zero-count pills when not searching and not selected.
            if (!hasSearch && count === 0 && activeService !== key) {
              return null;
            }
            // When user IS searching, also hide zero-count pills that
            // aren't the currently active selection (existing behavior).
            if (hasSearch && count === 0 && activeService !== key) {
              return null;
            }
            const config = SERVICE_COLORS[key];
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => handleServiceFilter(key)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all active:scale-95 ${
                  activeService === key
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`filter-${key}`}
              >
                <Icon className="h-3 w-3" />
                {label}{countsLoaded ? ` (${count.toLocaleString()})` : ''}
              </button>
            );
          })}
        </AutoScrollPills>

        {/* Action Hub — shown only on "All" pill when search is empty.
            Reuses existing FeedCard, FeaturedBanner, SponsoredCard, news +
            agents APIs. Hides automatically as soon as user types in the
            search box or switches to any other service pill. */}
        {activeService === 'all' && !search.trim() && (
          <ActionHub
            navigate={navigate}
            items={items}
            setSearch={setSearch}
            setActiveService={handleServiceFilter}
            sortBy={sortBy}
            fetchFeed={fetchFeed}
          />
        )}

        {/* Sponsored */}
        <div className="mb-3">
          <AdCarousel key={`carousel-${adSection}`} section={adSection} />
        </div>

        {/* Results count + Sort/Filter dropdown — same row, right-aligned */}
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-xs text-slate-400" data-testid="explore-results-count">
            {loading ? '...' : `Scroll to see ${totalCount.toLocaleString()} results`}
          </p>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setShowSortMenu(s => !s)}
              className={`px-3 py-1.5 rounded-full border text-xs font-bold flex items-center gap-1.5 transition-all ${
                sortBy !== 'featured'
                  ? 'border-[#2F80ED] bg-blue-50 text-[#2F80ED]'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
              data-testid="sort-toggle"
            >
              <Filter className="h-3 w-3" />
              {{featured:'Featured', price_asc:'Price ↑', price_desc:'Price ↓', rating:'Rating', newest:'Newest', popular:'Popular'}[sortBy] || 'Sort'}
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl border border-slate-200 shadow-xl z-20 py-1 overflow-hidden" data-testid="sort-menu">
                  {[
                    { key: 'featured',    label: 'Featured', desc: 'Sponsored + Boosted first' },
                    { key: 'price_asc',   label: 'Price: Low → High', desc: '' },
                    { key: 'price_desc',  label: 'Price: High → Low', desc: '' },
                    { key: 'rating',      label: 'Top Rated', desc: '' },
                    { key: 'newest',      label: 'Newest First', desc: '' },
                    { key: 'popular',     label: 'Most Popular', desc: '' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setSortBy(opt.key); setShowSortMenu(false); }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        sortBy === opt.key ? 'bg-blue-50 text-[#2F80ED] font-bold' : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      data-testid={`sort-option-${opt.key}`}
                    >
                      <div className="font-semibold">{opt.label}</div>
                      {opt.desc && <div className="text-[10px] text-slate-400 mt-0.5">{opt.desc}</div>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Feed Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-52 shimmer rounded-2xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Search className="h-12 w-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No results found</p>
            <p className="text-slate-300 text-xs mt-1">Try different keywords or filters</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="explore-feed-grid">
              {items.map((item, idx) => (
                <React.Fragment key={`${item.service_type}-${item.id}-${idx}`}>
                  <FeedCard item={item} navigate={navigate} />
                  {idx === 2 && <SponsoredCard key={`sp0-${adSection}`} section={adSection} index={0} />}
                  {idx === 5 && <FeaturedBanner key={`fb-${adSection}`} section={adSection} index={0} />}
                  {idx === 9 && <RecommendedStrip key={`rec-${adSection}`} section={adSection} />}
                  {idx === 12 && <SponsoredCard key={`sp1-${adSection}`} section={adSection} index={1} />}
                </React.Fragment>
              ))}
            </div>
            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="flex justify-center py-6" data-testid="explore-load-more">
                {loadingMore && <Loader2 className="h-6 w-6 text-[#2F80ED] animate-spin" />}
              </div>
            )}
            {!hasMore && items.length > 0 && (
              <p className="text-center text-xs text-slate-300 py-4">All {totalCount.toLocaleString()} results loaded</p>
            )}
          </>
        )}
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shimmer { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
        @keyframes slideDown { 0% { opacity: 0; transform: translateY(-12px); } 100% { opacity: 1; transform: translateY(0); } }
        .animate-slideDown { animation: slideDown 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
}

/**
 * AutoScrollPills — horizontal pill row that gently auto-scrolls but
 * remains fully swipeable. On mobile (<lg) we drive `scrollLeft` via
 * requestAnimationFrame; on desktop we just wrap, no animation.
 *
 *   • Auto-scroll speed: 25 px / sec (slow, peripheral)
 *   • Pauses on touch / pointerdown / wheel / keyboard focus
 *   • Resumes after 2.5 s of inactivity
 *   • Seamless infinite loop via duplicated content + scrollLeft snap
 *   • Respects prefers-reduced-motion
 *   • Pills accept clicks the entire time — no pointer-events overrides
 */
function AutoScrollPills({ all, children }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const primaryRef = useRef(null);
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);
  const pausedUntilRef = useRef(0);
  const positionRef = useRef(0);     // sub-pixel accumulator
  const programmaticRef = useRef(false);  // suppress self-triggered scroll events
  // Mirror is only useful when the primary set actually overflows the viewport.
  // When the user filters down to 1–2 pills, the row fits naturally and the
  // mirror would render side-by-side, producing visible duplicates.
  const [shouldMirror, setShouldMirror] = useState(false);

  // Re-measure overflow whenever children change or window resizes.
  useEffect(() => {
    const wrap = wrapRef.current;
    const primary = primaryRef.current;
    if (!wrap || !primary) return;

    const measure = () => {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      if (isDesktop) {
        setShouldMirror(false);
        return;
      }
      // Add a small buffer so we don't flicker on near-fits.
      const overflows = primary.scrollWidth > wrap.clientWidth + 8;
      setShouldMirror(overflows);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    ro.observe(primary);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [children, all]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    // Disable on desktop or if user prefers reduced motion
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
    if (reduceMotion) return;

    const SPEED_PX_PER_SEC = 32;       // visible but unobtrusive
    const PAUSE_MS = 2500;

    const tick = (now) => {
      if (!lastTickRef.current) lastTickRef.current = now;
      const dt = Math.min(now - lastTickRef.current, 100);
      lastTickRef.current = now;

      // Only auto-scroll when content actually overflows (mirror is rendered)
      // and we're on mobile. Otherwise the loop math wraps a non-loopable row.
      if (!isDesktop() && now > pausedUntilRef.current && wrap.scrollWidth > wrap.clientWidth + 8) {
        const half = inner.scrollWidth / 2;
        if (half > 0) {
          positionRef.current += (SPEED_PX_PER_SEC * dt) / 1000;
          if (positionRef.current >= half) positionRef.current -= half;
          programmaticRef.current = true;
          wrap.scrollLeft = positionRef.current;  // browser floors internally
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const pause = () => {
      pausedUntilRef.current = performance.now() + PAUSE_MS;
      // Sync accumulator so resume picks up from where the user left off
      positionRef.current = wrap.scrollLeft;
    };
    const onScroll = () => {
      if (programmaticRef.current) {
        programmaticRef.current = false;
        return;
      }
      pause();
    };
    wrap.addEventListener('pointerdown', pause, { passive: true });
    wrap.addEventListener('touchstart', pause, { passive: true });
    wrap.addEventListener('wheel', pause, { passive: true });
    wrap.addEventListener('focusin', pause);
    wrap.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafRef.current);
      wrap.removeEventListener('pointerdown', pause);
      wrap.removeEventListener('touchstart', pause);
      wrap.removeEventListener('wheel', pause);
      wrap.removeEventListener('focusin', pause);
      wrap.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="flex overflow-x-auto scrollbar-hide pb-3 -mx-4 px-4 lg:overflow-visible lg:mx-0 lg:px-0 mb-3"
      data-testid="service-filter-pills"
      style={{ scrollBehavior: 'auto' }}
    >
      <div
        ref={innerRef}
        className="flex gap-2 lg:flex-wrap lg:w-full"
        style={{ width: 'max-content' }}
      >
        {/* Primary set — the testids live here, always visible */}
        <div ref={primaryRef} className="flex gap-2 shrink-0 lg:contents">
          {all}
          {children}
        </div>
        {/* Mirror set — only rendered when primary overflows so few-pill
            states don't show visible duplicates. Hidden on desktop. */}
        {shouldMirror && (
          <div className="flex gap-2 shrink-0 lg:hidden" aria-hidden="true">
            {all && React.cloneElement(all, { 'data-testid': undefined, tabIndex: -1 })}
            {React.Children.map(children, (child) => (
              child ? React.cloneElement(child, { 'data-testid': undefined, tabIndex: -1 }) : null
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
